import { Box, Grid2, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import { FunctionComponent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "../../../app/helpers";
import { RootState } from "../../../app/rootReducer";
import Illustration from "../../../features/Booking-Form/assets/mobile-working-illustration.svg";
import { DisplayFloorPlanView } from "../../FacilityManager/Components/Views/DisplayFloorplanView/DisplayFloorPlanView";
import { IFloorPayload } from "../../FacilityManager/Domain/Types/FloorPlan/FloorPayload.type";
import DefaultSpinner from "../../LoadingSpinner/default-spinner.component";
import { modeVisitor, modeWorkplace, modeZone } from "../functions/confirm.functions";
import { handleFloorFetch, handleResourceFloorFetch } from "../functions/floor.functions";
import {
  conferenceBookingHasVisitorInvitations,
  teamBookingHasVisitorInvitations
} from "../functions/team-visitor-invitation.functions";
import { useBackgroundImage } from "../../FacilityManager/Hooks/useBackgroundImage";
import { FormConfirmingSpinner } from "../partial/form-confirming-spinner.partial";
import { FormVisitorConfirm } from "../form-visitor-confirm-booking.component";

interface OwnProps {
  confirmingBooking: boolean;
  isResourceBooking?: boolean;
  floorInventoryId?: number;
}

type Props = OwnProps;

const FormConfirmFloorPlan: FunctionComponent<Props> = ({
  confirmingBooking,
  isResourceBooking = false,
  floorInventoryId
}) => {
  const { inputs, floors } = useSelector((state: RootState) => state.booking);
  const [floorPlan, setFloorPlan] = useState<IFloorPayload>();
  const minHeightView = "70vh";
  const theme = useTheme();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  const { background, isEmptyBgUsed } = useBackgroundImage({
    backgroundImageUrl: floorPlan?.outlineUrl,
    viewport: floorPlan?.viewport
  });

  useEffect(() => {
    if (isResourceBooking && floorInventoryId != null) {
      handleResourceFloorFetch(inputs, floorInventoryId, setFloorPlan);
    } else {
      handleFloorFetch(inputs, floors, setFloorPlan);
    }
  }, [floors, inputs, isResourceBooking, floorInventoryId]);

  const dynamicHeight = isMobile ? minHeightView : "60vh";
  const showTeamVisitorSummary =
    teamBookingHasVisitorInvitations(inputs) || conferenceBookingHasVisitorInvitations(inputs);

  // when mobile working is selected, just display an illustration depcting that
  if (inputs.isMobileWorking) {
    return <img style={{ width: "100%" }} src={Illustration} alt="illu" />;
  }

  if (inputs.automatedSeating) {
    return (
      <Box
        sx={{
          mt: "10px",
          height: "500px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center"
        }}
      >
        <img alt={"illu_confusion"} src={"/confusion.png"} height={"300px"} />
        <Box margin={2}>
          <Typography variant={"body2"}>{t("afterSelectMobileWorking")}</Typography>
        </Box>
      </Box>
    );
  }

  if (confirmingBooking) {
    if (showTeamVisitorSummary) {
      return <FormVisitorConfirm confirmingBooking bookingInputs={inputs} />;
    }
    return <FormConfirmingSpinner />;
  }

  if (floorPlan && background) {
    const modeWorkplaceOrZone = modeWorkplace(inputs) || modeZone(inputs) || modeVisitor(inputs);
    const highlightedInventories = checkInventoryIdInBookingObj(modeWorkplaceOrZone, floorPlan);

    return (
      <Stack spacing={1} sx={{ width: "100%" }} data-testid="form-confirm-floor-plan-stack">
        {showTeamVisitorSummary && (
          <FormVisitorConfirm confirmingBooking={false} bookingInputs={inputs} />
        )}
        <Grid2 style={{ width: "100%", height: dynamicHeight }}>
          {/* Confirm omits highlightedOccupancyIcon so wizard/login occupancy stays (`undefined`, not ""). */}
          <DisplayFloorPlanView
            data-testid={"fm-basic-view"}
            background={background}
            isEmptyBgUsed={isEmptyBgUsed}
            name={"booking"}
            floorPlan={floorPlan as IFloorPayload}
            seatStatus={{
              availableList: highlightedInventories,
              disableList: [],
              occupiedList: [],
              restrictedList: [],
              userOccupiedList: highlightedInventories
            }}
          />
        </Grid2>
      </Stack>
    );
  }

  return <DefaultSpinner />;
};

export default FormConfirmFloorPlan;

function definedInventoryIds(ids: Array<number | undefined>): number[] {
  return ids.filter((id): id is number => id != null);
}

export function checkInventoryIdInBookingObj(
  bookingObject: number[] | undefined,
  floorPlan: IFloorPayload
) {
  if (!bookingObject?.length) return [];

  const bookedPlace = floorPlan.places.filter(wp =>
    bookingObject.includes(wp.inventoryId || wp.inventory?.id || wp.id)
  );
  if (bookedPlace.length > 0) {
    return definedInventoryIds(bookedPlace.map(p => p.inventoryId || p.inventory?.id));
  }

  const bookedZone = floorPlan.zones.filter(zn =>
    bookingObject.includes(zn.inventory?.id || zn.inventoryId)
  );
  if (bookedZone.length > 0) {
    return definedInventoryIds(bookedZone.map(z => z.inventory?.id || z.inventoryId));
  }

  return [];
}
